import React, { useRef, useMemo } from 'react';
import { Menu, menuSection } from '../../components/Menu';
import type { MenuItem, MenuItemRadio, MenuItemCheckbox, MenuItemAction, MenuItemSubmenu } from '../../components/Menu';
import type { MediaSource } from '../types';
import type { SafeAreaKind, SafeAreaSelection } from '../hooks/useVideoOverlays';
import { PlayerButton } from './PlayerButton';

const SPEED_OPTIONS = [0.5, 1, 1.5, 2, 4];

export interface MatteOption {
  label: string;
  value: number | null;
}

export const MATTE_OPTIONS: MatteOption[] = [
  { label: 'None', value: null },
  { label: '0.5 (1:2)', value: 0.5 },
  { label: '1.33 (4:3)', value: 4 / 3 },
  { label: '1.5 (3:2)', value: 1.5 },
  { label: '1.56 (14:9)', value: 14 / 9 },
  { label: '1.78 (16:9)', value: 16 / 9 },
  { label: '1.85', value: 1.85 },
  { label: '2.35', value: 2.35 },
  { label: '2.40', value: 2.40 },
];

export const SAFE_AREA_OPTIONS: { label: string; value: SafeAreaKind }[] = [
  { label: '4:3', value: '4:3' },
  { label: '16:9', value: '16:9' },
];

const AR_EPSILON = 0.01;

export function matteIsActive(optionValue: number | null, activeMatte: number | null): boolean {
  if (optionValue === null && activeMatte === null) return true;
  if (optionValue === null || activeMatte === null) return false;
  return Math.abs(optionValue - activeMatte) < AR_EPSILON;
}

export interface OverflowMenuItem {
  id: string;
  label: string;
  type: 'action' | 'toggle';
  isActive?: boolean;
  onClick: () => void;
}

interface ActionsMenuProps {
  isOpen: boolean;
  playbackRate: number;
  isLooping: boolean;
  sources: MediaSource[];
  activeSourceIndex: number;
  overflowItems?: OverflowMenuItem[];
  onToggle: () => void;
  onSpeedChange: (rate: number) => void;
  onSourceChange: (index: number) => void;
  onToggleLoop: () => void;
  onOpenSettings: () => void;
  overlayMatte?: number | null;
  overlaySafeAreas?: SafeAreaSelection;
  onOverlayMatteChange?: (value: number | null) => void;
  onToggleOverlaySafeArea?: (value: SafeAreaKind) => void;
}

export function ActionsMenu({
  isOpen,
  playbackRate,
  isLooping,
  sources,
  activeSourceIndex,
  overflowItems,
  onToggle,
  onSpeedChange,
  onSourceChange,
  onToggleLoop,
  onOpenSettings,
  overlayMatte,
  overlaySafeAreas,
  onOverlayMatteChange,
  onToggleOverlaySafeArea,
}: ActionsMenuProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);

  const items = useMemo<MenuItem[]>(() => {
    const result: MenuItem[] = [];

    // Playback Speed section
    const speedItems: MenuItemRadio[] = SPEED_OPTIONS.map((speed) => ({
      type: 'radio' as const,
      id: `speed-${speed}`,
      label: `${speed}x`,
      checked: playbackRate === speed,
      onClick: () => onSpeedChange(speed),
    }));
    result.push(...menuSection('Playback Speed', speedItems));

    // Loop Playback (standalone checkbox after speed separator)
    const loopItem: MenuItemCheckbox = {
      type: 'checkbox',
      id: 'loop-playback',
      label: 'Loop Playback',
      checked: isLooping,
      onClick: onToggleLoop,
    };
    result.push(loopItem);
    result.push({ type: 'separator', id: 'sep-after-loop' });

    // Quality section (only if multiple sources)
    if (sources.length > 1) {
      const sourceItems: MenuItemRadio[] = sources.map((source, idx) => ({
        type: 'radio' as const,
        id: `source-${idx}`,
        label: source.label,
        checked: activeSourceIndex === idx,
        onClick: () => onSourceChange(idx),
      }));
      result.push(...menuSection('Quality', sourceItems));
    }

    // Controls section (overflow items)
    if (overflowItems && overflowItems.length > 0) {
      const controlItems: MenuItem[] = overflowItems.map((item) => {
        if (item.type === 'toggle') {
          return {
            type: 'checkbox' as const,
            id: `overflow-${item.id}`,
            label: item.label,
            checked: item.isActive ?? false,
            onClick: item.onClick,
          } satisfies MenuItemCheckbox;
        }
        return {
          type: 'action' as const,
          id: `overflow-${item.id}`,
          label: item.label,
          onClick: item.onClick,
        } satisfies MenuItemAction;
      });
      result.push(...menuSection('Controls', controlItems));
    }

    // Overlays section (submenus for Mattes and Safe Areas)
    if (onOverlayMatteChange && onToggleOverlaySafeArea) {
      const activeMatte = overlayMatte ?? null;
      const activeSafeAreas = overlaySafeAreas ?? [];

      const mattesChildren: MenuItemRadio[] = MATTE_OPTIONS.map((opt) => ({
        type: 'radio' as const,
        id: `matte-${opt.label}`,
        label: opt.label,
        checked: matteIsActive(opt.value, activeMatte),
        onClick: () => onOverlayMatteChange(opt.value),
      }));

      const safeAreasChildren: MenuItemCheckbox[] = SAFE_AREA_OPTIONS.map((opt) => ({
        type: 'checkbox' as const,
        id: `safe-area-${opt.value}`,
        label: opt.label,
        checked: activeSafeAreas.includes(opt.value),
        onClick: () => onToggleOverlaySafeArea(opt.value),
      }));

      const mattesSubmenu: MenuItemSubmenu = {
        type: 'submenu',
        id: 'overlays-mattes',
        label: 'Mattes',
        children: mattesChildren,
      };

      const safeAreasSubmenu: MenuItemSubmenu = {
        type: 'submenu',
        id: 'overlays-safe-areas',
        label: 'Safe Areas',
        children: safeAreasChildren,
      };

      result.push(...menuSection('Overlays', [mattesSubmenu, safeAreasSubmenu]));
    }

    // Playback Settings action
    const settingsItem: MenuItemAction = {
      type: 'action',
      id: 'playback-settings',
      label: 'Playback Settings\u2026',
      onClick: onOpenSettings,
    };
    result.push(settingsItem);

    return result;
  }, [
    playbackRate,
    isLooping,
    sources,
    activeSourceIndex,
    overflowItems,
    onSpeedChange,
    onSourceChange,
    onToggleLoop,
    onOpenSettings,
    overlayMatte,
    overlaySafeAreas,
    onOverlayMatteChange,
    onToggleOverlaySafeArea,
  ]);

  return (
    <div className="kollab-player-actions-menu-container">
      <PlayerButton
        buttonRef={triggerRef}
        className={`kollab-player-actions-btn ${isOpen ? 'kollab-player-actions-btn--active' : ''}`}
        tooltip="Actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={onToggle}
      />

      <Menu
        items={items}
        isOpen={isOpen}
        onClose={onToggle}
        ariaLabel="Actions"
        triggerRef={triggerRef}
        className="kollab-player-actions-dropdown"
      />
    </div>
  );
}
